import { Helmet } from "react-helmet-async";
import { useLocation } from "wouter";
import { useBranding } from "../contexts/BrandingContext";
import { getSEOConfig } from "../config/brandSeoConfig";

interface BrandSEOManagerProps {
  // Optional overrides for specific pages
  titleOverride?: string;
  descriptionOverride?: string;
  keywordsOverride?: string;
  imageOverride?: string;
  schemaOverride?: any;
  /**
   * Explicit origin (e.g. 'https://www.greenshieldpm.co.uk').
   * Falls back to window.location.origin at runtime, or empty string
   * when window is unavailable (build-time / server contexts).
   */
  origin?: string;
  /**
   * Explicit canonical URL — overrides auto-detection from window.location.
   * Useful when the component is rendered outside a live browser context.
   */
  canonicalUrl?: string;
}

export default function BrandSEOManager({
  titleOverride,
  descriptionOverride,
  keywordsOverride,
  imageOverride,
  schemaOverride,
  origin: originProp,
  canonicalUrl: canonicalUrlProp,
}: BrandSEOManagerProps) {
  const [location] = useLocation();
  const { currentBrand } = useBranding();

  // Get SEO config for current route and brand
  const seoConfig = getSEOConfig(location, currentBrand.type);

  // Apply overrides if provided
  const finalTitle = titleOverride || seoConfig.title;
  const finalDescription = descriptionOverride || seoConfig.description;
  const finalKeywords = keywordsOverride || seoConfig.keywords;
  const finalRobots = seoConfig.robots || "index, follow";

  // Safe origin: prop > window.location.origin > empty string
  // Empty string is safe – meta tags will use relative-like values in dev
  const safeOrigin: string =
    originProp ?? (typeof window !== "undefined" ? window.location.origin : "");

  // Brand-specific image handling with full URL
  const getOGImage = () => {
    if (imageOverride) return imageOverride;
    return `${safeOrigin}/greenshield-logo.png`;
  };

  // Ensure robust fallbacks for meta content
  const finalCanonical =
    canonicalUrlProp ??
    seoConfig.canonical ??
    (typeof window !== "undefined"
      ? window.location.href
      : safeOrigin + location);
  const finalOGTitle = finalTitle;
  const finalOGDescription = finalDescription;

  // Generate brand-specific structured data
  const generateStructuredData = () => {
    const baseUrl = safeOrigin;

    if (schemaOverride) {
      return schemaOverride;
    }

    switch (seoConfig.schemaType) {
      case "SoftwareApplication":
        return {
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Aegis360 Pest Control Software",
          description: "Pest control business management platform",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web Browser",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "GBP",
            description: "Free trial available",
          },
          publisher: {
            "@type": "Organization",
            name: "Aegis360",
            url: "https://www.aegis360.co.uk",
            logo: {
              "@type": "ImageObject",
              url: `${baseUrl}/aegis360-logo.jpg`,
            },
          },
          featureList: [
            "Customer Management",
            "Automated Scheduling",
            "Route Optimization",
            "Business Analytics",
            "Operational Insights",
            "Mobile App Integration",
          ],
          screenshot: `${baseUrl}/aegis360-dashboard-screenshot.png`,
          downloadUrl: `${baseUrl}/book-demo`,
          softwareVersion: "2.0",
          dateModified: new Date().toISOString(),
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            reviewCount: "150",
          },
        };

      case "LocalBusiness":
        return {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `${baseUrl}/#business`,
          name: "Greenshield Pest Management",
          description:
            "Professional eco-friendly pest control services with proactive monitoring and sustainable methods",
          url: baseUrl,
          telephone: "07913024492",
          email: "info@greenshieldpestmanagement.co.uk",
          address: {
            "@type": "PostalAddress",
            addressLocality: "United Kingdom",
            addressCountry: "GB",
            postalCode: "Various",
            streetAddress: "Serving all UK locations",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: "51.5074",
            longitude: "-0.1278",
          },
          openingHours: "Mo-Su 00:00-23:59",
          priceRange: "££",
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "5.0",
            reviewCount: "500",
            bestRating: "5",
            worstRating: "1",
          },
          serviceArea: {
            "@type": "Country",
            name: "United Kingdom",
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Pest Control Services",
            itemListElement: [
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Residential Pest Control",
                  description:
                    "Comprehensive pest control for homes and residential properties",
                  provider: {
                    "@type": "LocalBusiness",
                    name: "Greenshield Pest Management",
                  },
                },
                priceSpecification: {
                  "@type": "PriceSpecification",
                  minPrice: "89",
                  priceCurrency: "GBP",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Commercial Pest Control",
                  description:
                    "Professional pest management for businesses and commercial properties",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Emergency Pest Control",
                  description: "24/7 emergency pest control response services",
                },
                priceSpecification: {
                  "@type": "PriceSpecification",
                  minPrice: "149",
                  priceCurrency: "GBP",
                },
              },
            ],
          },
          logo: `${baseUrl}/greenshield-logo.png`,
          image: [
            `${baseUrl}/greenshield-logo.png`,
            `${baseUrl}/pest-control-service.jpg`,
          ],
          sameAs: [
            "https://facebook.com/greenshieldpest",
            "https://twitter.com/greenshieldpest",
            "https://linkedin.com/company/greenshield-pest-management",
          ],
          founder: {
            "@type": "Person",
            name: "Iredia Anthony",
          },
          foundingDate: "2020",
          numberOfEmployees: "10-50",
          knowsAbout: [
            "Pest Control",
            "Eco-friendly Treatments",
            "Proactive Monitoring",
            "Emergency Response",
            "Commercial Pest Management",
            "Residential Pest Control",
          ],
        };

      case "Organization":
        const orgName =
          currentBrand.type === "aegis360"
            ? "Aegis360"
            : "Greenshield Pest Management";
        const orgDescription =
          currentBrand.type === "aegis360"
            ? "Leading provider of pest control business management software"
            : "Professional eco-friendly pest control services across the UK";

        return {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: orgName,
          description: orgDescription,
          url: baseUrl,
          logo: {
            "@type": "ImageObject",
            url: getOGImage(),
          },
          contactPoint: {
            "@type": "ContactPoint",
            telephone:
              currentBrand.type === "greenshield"
                ? "07913024492"
                : "+44-800-AEGIS360",
            contactType: "customer service",
            availableLanguage: "English",
          },
          sameAs: [
            `https://facebook.com/${currentBrand.type}`,
            `https://twitter.com/${currentBrand.type}`,
            `https://linkedin.com/company/${currentBrand.type}`,
          ],
        };

      case "Service":
        return {
          "@context": "https://schema.org",
          "@type": "Service",
          name: finalTitle,
          description: finalDescription,
          provider: {
            "@type":
              currentBrand.type === "aegis360"
                ? "Organization"
                : "LocalBusiness",
            name: currentBrand.name,
            telephone:
              currentBrand.type === "greenshield"
                ? "07913024492"
                : "+44-800-AEGIS360",
          },
          areaServed: {
            "@type": "Country",
            name: "United Kingdom",
          },
          availableChannel: {
            "@type": "ServiceChannel",
            availableLanguage: "English",
            servicePhone:
              currentBrand.type === "greenshield"
                ? "07913024492"
                : "+44-800-AEGIS360",
            serviceUrl: baseUrl,
          },
          category:
            currentBrand.type === "aegis360" ? "Software" : "Pest Control",
        };

      case "Article":
        return {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: finalTitle,
          description: finalDescription,
          author: {
            "@type": "Organization",
            name: currentBrand.name,
          },
          publisher: {
            "@type": "Organization",
            name: currentBrand.name,
            logo: {
              "@type": "ImageObject",
              url: getOGImage(),
            },
          },
          datePublished: "2026-01-01T00:00:00.000Z",
          dateModified: "2026-01-01T00:00:00.000Z",
          image: getOGImage(),
        };

      case "WebPage":
      default:
        return {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: finalTitle,
          description: finalDescription,
          url: finalCanonical,
          isPartOf: {
            "@type": "WebSite",
            name: currentBrand.name,
            url: baseUrl,
          },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: baseUrl,
              },
            ],
          },
        };
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content={finalRobots} />

      {/* Canonical URL */}
      <link rel="canonical" href={finalCanonical} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={finalOGTitle} />
      <meta property="og:description" content={finalOGDescription} />
      <meta property="og:image" content={getOGImage()} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={currentBrand.name} />
      <meta property="og:url" content={finalCanonical} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalOGTitle} />
      <meta name="twitter:description" content={finalOGDescription} />
      <meta name="twitter:image" content={getOGImage()} />
      <meta
        name="twitter:site"
        content={
          currentBrand.type === "aegis360" ? "@aegis360" : "@greenshieldpest"
        }
      />

      {/* Additional SEO Meta Tags */}
      <meta name="author" content={currentBrand.name} />
      <meta name="language" content="en-GB" />
      <meta name="revisit-after" content="7 days" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Brand-specific meta tags */}
      {currentBrand.type === "greenshield" && (
        <>
          <meta name="geo.region" content="GB" />
          <meta name="geo.placename" content="United Kingdom" />
          <meta name="ICBM" content="51.5074, -0.1278" />
        </>
      )}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(generateStructuredData(), null, 2)}
      </script>
    </Helmet>
  );
}
